type VisibilityCallback = () => unknown;

const MINIMUM_TIME_ON_SCREEN_IN_MILLIS = 100;

/**
 * Observes if an element is visible in the viewport for a specified duration of time.
 *
 * @param {Element | null} targetElement - The target DOM element to observe. If `null`, the function does nothing.
 * @param {number} requiredTime - The time (in milliseconds) the element needs to stay in the viewport to trigger the callback.
 * @param {() => void} onVisibilityMet - A callback function that is executed once the element has been visible for the required time.
 *
 * @returns {() => void} A cleanup function that stops observing the element and clears any active timers.
 * Call this when you no longer need to track the element.
 *
 * @example
 * const element = document.querySelector('.watched-element');
 * const cleanup = trackElementVisibility(element, 2000, () => {
 *   console.log('Element has been visible for 2 seconds!');
 * });
 */
export const observeElementVisibility = (
  targetElement: Element,
  requiredTime: number,
  onVisibilityMet: VisibilityCallback,
): (() => void) => {
  let timer: number | null = null;
  let timeInView = 0;

  const startTimer = (): void => {
    if (timer !== null) {
      return;
    }

    timer = window.setInterval(() => {
      timeInView += MINIMUM_TIME_ON_SCREEN_IN_MILLIS;
      if (timeInView >= requiredTime) {
        onVisibilityMet();
        stopTimer();
      }
    }, MINIMUM_TIME_ON_SCREEN_IN_MILLIS);
  };

  const stopTimer = (): void => {
    if (timer !== null) {
      clearInterval(timer);
      timer = null;
    }
    timeInView = 0;
  };

  const handleIntersect: IntersectionObserverCallback = (entries) => {
    const entry = entries[0];
    if (entry.isIntersecting) {
      startTimer();
    } else {
      stopTimer();
    }
  };

  const observer = new IntersectionObserver(handleIntersect);

  if (targetElement) {
    observer.observe(targetElement);
  }

  return (): void => {
    observer.disconnect();
    stopTimer();
  };
};
